import { init, setContext, setUserId, track as send } from "@bk/app-events";
import { useUserInfoStore } from "@/store/userInfoStore";
import { isTelegramWebApp } from "@/utils/telegram";
import versionInfo from "../../public/assets/version.json";
import type { Trigger } from "./triggers";

export { dataTrigger, track } from "./triggers";
export type { Trigger } from "./triggers";

const OPEN: Trigger = "ref.app.open";
const OPENED_KEY = "ref:app-events:opened";

function launchContext(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.slice(1));
  const param = (name: string) => params.get(name) ?? hashParams.get(name);
  const isTelegramLaunch = Boolean(
    param("tgWebAppPlatform") || param("tgWebAppData")
  );
  const context: Record<string, string> = {
    platform: isTelegramWebApp() || isTelegramLaunch ? "tgapp" : "web",
    version: versionInfo.version,
  };

  const serviceType = param("service_type");
  if (serviceType) context.service_type = serviceType;

  const startParam =
    param("start_param") ??
    param("tgWebAppStartParam") ??
    param("startapp") ??
    param("start");
  if (startParam) context.start_param = startParam;

  params.forEach((value, key) => {
    if (key.startsWith("utm_") && value) context[key] = value;
  });
  hashParams.forEach((value, key) => {
    if (key.startsWith("utm_") && value) context[key] = value;
  });

  return context;
}

function hasOpenedInSession(): boolean {
  try {
    return window.sessionStorage.getItem(OPENED_KEY) === "1";
  } catch {
    return false;
  }
}

function markOpenedInSession() {
  try {
    window.sessionStorage.setItem(OPENED_KEY, "1");
  } catch {
    // Without sessionStorage, every page load is treated as an app opening.
  }
}

export function initAnalytics() {
  if (!__APP_EVENTS_KEY__) return;

  const params = new URLSearchParams(window.location.search);
  const state = useUserInfoStore.getState();
  const resolvesYandexIdFromTelegram = Boolean(params.get("telegram_id"));
  const initialUserId =
    params.get("yandex_id") ||
    (resolvesYandexIdFromTelegram ? null : state.yandexId || null);
  const shouldOpen = !hasOpenedInSession();

  if (shouldOpen && initialUserId) markOpenedInSession();

  init({
    app: "ref",
    key: __APP_EVENTS_KEY__,
    userId: initialUserId,
    context: {
      ...launchContext(),
      ...(state.serviceType ? { service_type: state.serviceType } : {}),
    },
    triggers: shouldOpen && initialUserId ? [OPEN] : [],
    dev: __IS_DEV__,
  });

  useUserInfoStore.subscribe((next, previous) => {
    if (next.yandexId !== previous.yandexId) {
      setUserId(next.yandexId || null);
      if (shouldOpen && next.yandexId && !hasOpenedInSession()) {
        markOpenedInSession();
        send(OPEN);
      }
    }
    if (next.serviceType !== previous.serviceType) {
      setContext({ service_type: next.serviceType });
    }
  });
}
